import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, FileText, CheckCircle2, AlertTriangle, Trash2 } from "lucide-react";

const CATEGORIES = [
  { value: "carns", label: "Carns" },
  { value: "peixos", label: "Peixos i marisc" },
  { value: "lactis", label: "Làctics" },
  { value: "verdures", label: "Verdures i fruita" },
  { value: "cereals", label: "Cereals i farina" },
  { value: "especies", label: "Espècies i condiments" },
  { value: "olis", label: "Olis i greixos" },
  { value: "begudes", label: "Begudes" },
  { value: "altres", label: "Altres" },
];

const UNITS = ["g", "kg", "ml", "l", "u", "cs", "cc"];

export default function PdfImportModal({ open, onClose, venueId, onImported }) {
  const [step, setStep] = useState("upload"); // upload | preview | done
  const [uploading, setUploading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [selected, setSelected] = useState([]);
  const [savedCount, setSavedCount] = useState(0);

  const handleClose = () => {
    setStep("upload");
    setIngredients([]);
    setSelected([]);
    setError(null);
    setSavedCount(0);
    onClose();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      let file_url;
      try {
        const uploadResult = await hosys.integrations.Core.UploadFile({ file });
        file_url = uploadResult.file_url;
      } catch (uploadErr) {
        throw new Error("Error pujant el fitxer: " + (uploadErr.message || "comprova la connexió"));
      }

      let response;
      try {
        response = await hosys.functions.invoke("importIngredientsPdf", {
          file_url,
          venue_id: venueId,
        }, { timeout: 120000 });
      } catch (invokeErr) {
        throw new Error("Error analitzant el document: " + (invokeErr.message || "error de connexió"));
      }

      const result = response.data;
      if (result.error) throw new Error(result.error);
      if (!result.ingredients || result.ingredients.length === 0) throw new Error("No s'han trobat ingredients al document");
      const items = result.ingredients.map((ing, i) => ({ ...ing, _id: i }));
      setIngredients(items);
      setSelected(items.map(i => i._id));
      setStep("preview");
    } catch (err) {
      setError(err.message || "Error processant el PDF");
    } finally {
      setUploading(false);
    }
  };

  const updateIngredient = (id, field, value) => {
    setIngredients(prev => prev.map(ing => ing._id === id ? { ...ing, [field]: value } : ing));
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleImport = async () => {
    const toImport = ingredients.filter(ing => selected.includes(ing._id));
    setImporting(true);
    const today = new Date().toISOString().split("T")[0];
    for (const ing of toImport) {
      await hosys.entities.Ingredient.create({
        venue_id: venueId,
        name: ing.name,
        supplier: ing.supplier,
        unit: ing.unit,
        unit_price: ing.unit_price,
        category: ing.category,
        previous_price: ing.unit_price,
        last_updated: today,
        active: true,
      });
    }
    setSavedCount(toImport.length);
    setStep("done");
    setImporting(false);
    onImported();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#7B2D3F]" />
            Importar ingredients des de PDF
          </DialogTitle>
        </DialogHeader>

        {/* STEP: UPLOAD */}
        {step === "upload" && (
          <div className="space-y-5">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
              Puja un PDF (albarà, factura o catàleg del proveïdor) amb els ingredients. El sistema extreu automàticament el nom, proveïdor, preu del paquet i quantitat per calcular el preu unitari.
            </div>
            <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-2xl p-10 cursor-pointer hover:border-[#7B2D3F]/40 hover:bg-[#7B2D3F]/5 transition-all" onClick={e => uploading && e.preventDefault()}>
              {uploading ? (
                <div className="flex flex-col items-center gap-3 text-[#7B2D3F]">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p className="font-semibold text-sm">Analitzant PDF amb IA...</p>
                  <p className="text-xs text-gray-400">Pot trigar uns segons</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3 text-gray-400">
                  <Upload className="w-8 h-8" />
                  <p className="font-semibold text-sm text-gray-600">Fes clic per pujar un PDF</p>
                  <p className="text-xs">PDF, JPG o PNG — Albarà, factura o catàleg</p>
                </div>
              )}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={handleFileChange} disabled={uploading} />
            </label>
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>
        )}

        {/* STEP: PREVIEW */}
        {step === "preview" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                S'han detectat <strong>{ingredients.length}</strong> ingredients. Revisa i selecciona els que vols importar.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelected(ingredients.map(i => i._id))}>Tots</Button>
                <Button variant="outline" size="sm" onClick={() => setSelected([])}>Cap</Button>
              </div>
            </div>
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
              {ingredients.map((ing) => (
                <div key={ing._id} className={`border rounded-xl p-3 transition-all ${selected.includes(ing._id) ? "border-[#7B2D3F]/30 bg-[#7B2D3F]/5" : "border-gray-100 bg-gray-50 opacity-60"}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selected.includes(ing._id)}
                      onChange={() => toggleSelect(ing._id)}
                      className="mt-1 accent-[#7B2D3F]"
                    />
                    <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      <div className="col-span-2 sm:col-span-1">
                        <p className="text-[10px] text-gray-400 font-semibold uppercase mb-0.5">Nom</p>
                        <Input
                          value={ing.name}
                          onChange={e => updateIngredient(ing._id, "name", e.target.value)}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase mb-0.5">Proveïdor</p>
                        <Input
                          value={ing.supplier || ""}
                          onChange={e => updateIngredient(ing._id, "supplier", e.target.value)}
                          className="h-8 text-sm"
                          placeholder="—"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase mb-0.5">Categoria</p>
                        <Select value={ing.category} onValueChange={v => updateIngredient(ing._id, "category", v)}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase mb-0.5">Preu paquet (€)</p>
                        <Input
                          type="number" step="0.01"
                          value={ing.package_price}
                          onChange={e => {
                            const pp = Number(e.target.value);
                            const up = ing.package_quantity > 0 ? Number((pp / ing.package_quantity).toFixed(4)) : 0;
                            setIngredients(prev => prev.map(x => x._id === ing._id ? { ...x, package_price: pp, unit_price: up } : x));
                          }}
                          className="h-8 text-sm"
                        />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase mb-0.5">Quantitat paquet</p>
                        <div className="flex gap-1">
                          <Input
                            type="number" step="0.01"
                            value={ing.package_quantity}
                            onChange={e => {
                              const pq = Number(e.target.value);
                              const up = pq > 0 ? Number((ing.package_price / pq).toFixed(4)) : 0;
                              setIngredients(prev => prev.map(x => x._id === ing._id ? { ...x, package_quantity: pq, unit_price: up } : x));
                            }}
                            className="h-8 text-sm"
                          />
                          <Select value={ing.unit} onValueChange={v => updateIngredient(ing._id, "unit", v)}>
                            <SelectTrigger className="h-8 w-20 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 font-semibold uppercase mb-0.5">Preu unitari (€/{ing.unit})</p>
                        <div className="h-8 flex items-center px-3 bg-emerald-50 border border-emerald-200 rounded-md text-sm font-bold text-emerald-700">
                          €{ing.unit_price?.toFixed(4) || "0.0000"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* STEP: DONE */}
        {step === "done" && (
          <div className="flex flex-col items-center justify-center py-10 gap-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <p className="text-lg font-bold text-gray-900">Importació completada!</p>
            <p className="text-sm text-gray-500">{savedCount} ingredient(s) afegit(s) a la base de dades.</p>
          </div>
        )}

        <DialogFooter>
          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")}>Tornar</Button>
              <Button
                onClick={handleImport}
                disabled={selected.length === 0 || importing}
                className="bg-[#7B2D3F] hover:bg-[#5B1D2F]"
              >
                {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Importar {selected.length > 0 ? `(${selected.length})` : ""}
              </Button>
            </>
          )}
          {(step === "upload" || step === "done") && (
            <Button variant="outline" onClick={handleClose}>
              {step === "done" ? "Tancar" : "Cancel·lar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}